import { BaseApiService } from '@/shared/api/services/base.service';
import type { JsonApiResource } from '@/shared/api/services/base.service';
import { apiClient } from '@/shared/lib/api';
import { resolveApiUrl } from '@/shared/lib/api-host';

/** Lifecycle of a cost estimate. Mirrors the API `status` attribute. */
export type EstimateStatus = 'pending' | 'generating' | 'ready' | 'failed';

/**
 * Export file formats supported by GET `/estimates/{id}/export`. The server's
 * `EstimateExportService::FORMATS` keys are `pdf` / `xlsx` / `txt`.
 */
export type EstimateExportFormat = 'pdf' | 'xlsx' | 'txt';

/** A downloaded export: the raw blob plus the server-supplied filename. */
export interface EstimateExportResult {
  blob: Blob;
  filename: string;
}

/** Which side of the estimate a line item belongs to. */
export type EstimateSection = 'materials' | 'labor';

/** Origin of a line item — AI-generated vs user-added/edited. */
export type EstimateItemSource = 'ai' | 'user';

export type MeasurementSystem = 'metric' | 'imperial';

/** Display unit for the facade area, server-resolved per measurement system. */
export type AreaUnit = 'm2' | 'ft2';

/**
 * One server-driven unit picker option: a display unit code of the estimate's
 * CURRENT measurement system plus its server-localized label. Clients never
 * hardcode the unit list or localize unit labels themselves.
 */
export interface UnitOption {
  code: string;
  label: string;
}

/**
 * Resolve the label to render for an item's unit code: null → em-dash (no
 * unit), a code missing from the options (legacy/stale data) degrades to the
 * raw code.
 */
export function resolveUnitLabel(
  options: UnitOption[],
  code: string | null
): string {
  if (code === null || code === '') return '—';
  return options.find((o) => o.code === code)?.label ?? code;
}

/**
 * A single estimate (materials + labor + totals), as returned by
 * GET `/estimates/{id}`. The line `items` are normalized out of the JSON:API
 * `included[]` (type `estimate_item`) into a flat array.
 */
export interface Estimate {
  id: string;
  status: EstimateStatus;
  language: string;
  country_code: string | null;
  /** Region snapshot the estimate was calculated with (display label). */
  region_label: string | null;
  /**
   * Project's CURRENT construction region display label (live project value;
   * `region_label` stays the estimate's snapshot). Powers the Region settings
   * row; pairs with `has_diverged_construction_region` for the regenerate
   * warning. Null when the project has no region set.
   */
  project_region_label: string | null;
  currency: string;
  measurement_system: MeasurementSystem;
  /**
   * Facade area snapshot the estimate was computed with, display-converted
   * into `facade_area_unit` server-side (clients do zero unit math).
   */
  facade_area: number | null;
  facade_area_unit: AreaUnit;
  /**
   * Project's CURRENT facade area in the same display unit (live project
   * value; `facade_area` stays the estimate's snapshot). Powers the Facade
   * area settings row; pairs with `has_diverged_facade_area` for the
   * regenerate warning. Null when the project has no area set.
   */
  project_facade_area: number | null;
  /**
   * Server-driven unit picker options in the current measurement system
   * (localized labels). Present on detail responses only.
   */
  unit_options: UnitOption[];
  /** Model's explanation of how the facade area was determined. */
  dimensions_notes: string | null;
  materials_notes: string | null;
  labor_notes: string | null;
  /**
   * Free-text user prompt injected into the generation LLM call (preferred
   * brands, works to include/exclude). A generation input like
   * currency/units: editing it never flips `is_edited`, and regeneration
   * keeps it.
   */
  special_requirements: string | null;
  estimated_duration_days: number | null;
  materials_total: number;
  labor_total: number;
  grand_total: number;
  is_edited: boolean;
  /**
   * Server-computed staleness flag: the concept gained completed renders AFTER
   * this estimate finished. Drives the "newer designs exist" warning banner —
   * clients must not re-derive this from timestamps.
   */
  has_newer_renders: boolean;
  /**
   * Server-computed flag: the project's facade area moved > 3% away from the
   * snapshot this estimate was calculated with. Drives the "facade area
   * changed, regenerate" warning — clients must not re-derive it from the
   * two area values.
   */
  has_diverged_facade_area: boolean;
  /**
   * Server-computed flag: the project's construction region was changed after
   * this estimate was generated. Drives the "region changed, regenerate"
   * warning — clients must not re-derive it from the two region values.
   */
  has_diverged_construction_region: boolean;
  failure_reason: string | null;
  created_at: string;
  items: EstimateItem[];
}

/** A line item inside an estimate. */
export interface EstimateItem {
  id: string;
  section: EstimateSection;
  category: string | null;
  name: string;
  quantity: number;
  unit: string | null;
  unit_price: number;
  line_total: number;
  sort_order: number;
  source: EstimateItemSource;
}

/**
 * Lightweight estimate row from the list endpoints (concept- and project-
 * scoped). The list endpoints return the full `EstimateResource`, so we pick
 * only the fields the carousel/cards need. `concept_id` lets the project-scoped
 * page navigate back to the owning concept's Estimate tab; `source_render_ids`
 * resolves the card thumbnail.
 */
export interface EstimateSummary {
  id: string;
  conceptId: string;
  /** Concept's project sequence number — the card's "Design #N" line. */
  conceptSequenceNumber: number | null;
  status: EstimateStatus;
  currency: string;
  region_label: string | null;
  materials_total: number;
  labor_total: number;
  grand_total: number;
  is_edited: boolean;
  /** Server-computed staleness flag — see `Estimate.has_newer_renders`. */
  has_newer_renders: boolean;
  source_render_ids: string[];
  created_at: string;
}

export interface CreateEstimateRequest {
  selected_renders: string[];
  language?: string;
  is_extended_thinking?: boolean;
  // Currency / measurement system are deliberately NOT sent on create: the
  // server seeds country-derived defaults and the generation re-derives them
  // from the LLM's display recommendation; an explicit value would pin them.
  /**
   * Free-text generation prompt (max 3000 chars server-side). Optional —
   * omitted when blank. Sent from the working intro's draft settings.
   */
  special_requirements?: string;
}

export interface UpdateEstimateRequest {
  materials_notes?: string | null;
  labor_notes?: string | null;
  /**
   * Free-text generation prompt (max 3000 chars server-side). Never flips
   * `is_edited`; explicit null / blank clears it.
   */
  special_requirements?: string | null;
  /**
   * Output language for the generated texts (album locale whitelist; 422 on
   * an unsupported code). A generation input: never flips `is_edited` —
   * existing texts change only on the next regenerate.
   */
  language?: string;
  /**
   * Display currency (3-letter ISO, uppercase). Sending it switches the
   * estimate's display currency — the server converts every amount and returns
   * the full converted estimate. Invalid/unsupported code → 422.
   */
  currency?: string;
  /**
   * Display measurement system. Sending it switches the estimate's display
   * system — the server converts every quantity/unit/unit price and returns
   * the full converted estimate. Unknown value → 422.
   */
  measurement_system?: MeasurementSystem;
}

export interface CreateEstimateItemRequest {
  section: EstimateSection;
  category?: string | null;
  name: string;
  quantity: number;
  unit?: string | null;
  unit_price: number;
  sort_order?: number;
  /**
   * Echo of the display currency the client denominated `unit_price` in.
   * The server rejects with 422 when it mismatches the estimate's current
   * currency (stale-device guard) — include it whenever sending `unit_price`.
   */
  currency?: string;
  /**
   * Echo of the display measurement system the client denominated `quantity`,
   * `unit`, and `unit_price` in. Same 422 stale-device guard as `currency` —
   * include it whenever sending any of those fields.
   */
  measurement_system?: MeasurementSystem;
}

export interface UpdateEstimateItemRequest {
  section?: EstimateSection;
  category?: string | null;
  name?: string;
  quantity?: number;
  unit?: string | null;
  unit_price?: number;
  sort_order?: number;
  /** Currency echo — see `CreateEstimateItemRequest.currency`. */
  currency?: string;
  /** Measurement-system echo — see `CreateEstimateItemRequest.measurement_system`. */
  measurement_system?: MeasurementSystem;
}

// JSON:API attribute shapes (id is top-level, never inside attributes).
type EstimateAttributes = Omit<Estimate, 'id' | 'items'>;
type EstimateItemAttributes = Omit<EstimateItem, 'id'>;
// The list endpoints return the full `EstimateResource`; we only consume the
// summary subset. `conceptId` is a top-level field on `EstimateSummary` derived
// from the `concept` relationship (it is not a server attribute).
type EstimateSummaryAttributes = Omit<
  EstimateSummary,
  'id' | 'conceptId' | 'conceptSequenceNumber'
>;

interface EstimateRelationships {
  concept?: {
    data?: { id?: string } | null;
    meta?: { project_sequence_number?: number | null };
  };
}

function mapItem(resource: JsonApiResource<EstimateItemAttributes>): EstimateItem {
  return { id: resource.id, ...resource.attributes };
}

function mapSummary(
  resource: JsonApiResource<EstimateSummaryAttributes>
): EstimateSummary {
  const relationships = resource.relationships as
    | EstimateRelationships
    | undefined;
  return {
    id: resource.id,
    conceptId: relationships?.concept?.data?.id ?? '',
    conceptSequenceNumber:
      relationships?.concept?.meta?.project_sequence_number ?? null,
    ...resource.attributes,
  };
}

/** JSON:API attributes of a `currency` resource from GET `/v1/currencies`. */
interface CurrencyAttributes {
  code?: string;
}

class EstimateService extends BaseApiService {
  /**
   * Supported display currencies for the estimate currency switcher.
   * Server returns USD first, then alphabetical — render verbatim, do not
   * re-sort.
   */
  async listCurrencies(): Promise<string[]> {
    const response = await this.httpGet<{
      data: JsonApiResource<CurrencyAttributes>[];
    }>('/v1/currencies');
    return response.data
      .map((r) => (r.attributes?.code ?? r.id ?? '').toUpperCase())
      .filter((code) => code.length > 0);
  }

  /** Concept-scoped list of estimates (summary rows). */
  async list(projectId: string, conceptId: string): Promise<EstimateSummary[]> {
    const response = await this.httpGet<{
      data: JsonApiResource<EstimateSummaryAttributes>[];
    }>(`/v1/projects/${projectId}/concepts/${conceptId}/estimates`);
    return response.data.map(mapSummary);
  }

  /** Project-scoped list of every estimate across all concepts (FE3 page). */
  async listByProject(projectId: string): Promise<EstimateSummary[]> {
    const response = await this.httpGet<{
      data: JsonApiResource<EstimateSummaryAttributes>[];
    }>(`/v1/projects/${projectId}/estimates`);
    return response.data.map(mapSummary);
  }

  /**
   * Full estimate with line items. Items arrive in `included[]` (type
   * `estimate_item`) and are normalized into a flat `items` array sorted by
   * `sort_order` so the editor renders a stable order.
   */
  async get(estimateId: string): Promise<Estimate> {
    const response = await this.httpGet<{
      data: JsonApiResource<EstimateAttributes>;
      included?: JsonApiResource<EstimateItemAttributes>[];
    }>(`/v1/estimates/${estimateId}`);

    const items = (response.included ?? [])
      .filter((r) => r.type === 'estimate_item')
      .map(mapItem)
      .sort((a, b) => a.sort_order - b.sort_order);

    return { id: response.data.id, ...response.data.attributes, items };
  }

  /** Start generation for the concept. Returns 202 pending. */
  async create(
    projectId: string,
    conceptId: string,
    data: CreateEstimateRequest
  ): Promise<EstimateSummary> {
    const response = await this.post<{
      data: JsonApiResource<EstimateSummaryAttributes>;
    }>(
      `/v1/projects/${projectId}/concepts/${conceptId}/estimates`,
      this.formatJsonApiData(
        'estimate',
        data as unknown as Record<string, unknown>
      )
    );
    return mapSummary(response.data);
  }

  /**
   * Re-run generation. When the estimate `is_edited` the server returns 409
   * (`COST_ESTIMATE_OVERWRITE_REQUIRED`) unless `confirm_overwrite` is set —
   * the caller surfaces a confirm dialog and retries with the flag.
   */
  async regenerate(
    estimateId: string,
    confirmOverwrite?: boolean
  ): Promise<void> {
    await this.post(
      `/v1/estimates/${estimateId}/regenerate`,
      this.formatJsonApiData('estimate', {
        ...(confirmOverwrite ? { confirm_overwrite: true } : {}),
      })
    );
  }

  /** Patch header fields (notes / duration / currency). Returns the updated estimate. */
  async update(
    estimateId: string,
    data: UpdateEstimateRequest
  ): Promise<Estimate> {
    const response = await this.patch<{
      data: JsonApiResource<EstimateAttributes>;
      included?: JsonApiResource<EstimateItemAttributes>[];
    }>(
      `/v1/estimates/${estimateId}`,
      this.formatJsonApiData(
        'estimate',
        data as unknown as Record<string, unknown>,
        undefined,
        estimateId
      )
    );
    const items = (response.included ?? [])
      .filter((r) => r.type === 'estimate_item')
      .map(mapItem)
      .sort((a, b) => a.sort_order - b.sort_order);
    return { id: response.data.id, ...response.data.attributes, items };
  }

  async createItem(
    estimateId: string,
    data: CreateEstimateItemRequest
  ): Promise<EstimateItem> {
    const response = await this.post<{
      data: JsonApiResource<EstimateItemAttributes>;
    }>(
      `/v1/estimates/${estimateId}/items`,
      this.formatJsonApiData(
        'estimate_item',
        data as unknown as Record<string, unknown>
      )
    );
    return mapItem(response.data);
  }

  async updateItem(
    estimateId: string,
    itemId: string,
    data: UpdateEstimateItemRequest
  ): Promise<EstimateItem> {
    const response = await this.patch<{
      data: JsonApiResource<EstimateItemAttributes>;
    }>(
      `/v1/estimates/${estimateId}/items/${itemId}`,
      this.formatJsonApiData(
        'estimate_item',
        data as unknown as Record<string, unknown>,
        undefined,
        itemId
      )
    );
    return mapItem(response.data);
  }

  async deleteItem(estimateId: string, itemId: string): Promise<void> {
    await this.httpDelete(`/v1/estimates/${estimateId}/items/${itemId}`);
  }

  /** Soft-delete the whole estimate (server broadcasts `estimates/deleted`). */
  async delete(estimateId: string): Promise<void> {
    await this.httpDelete(`/v1/estimates/${estimateId}`);
  }

  /**
   * Reorder one section's items: `itemIds` is the COMPLETE id set of that
   * section in the new order (the server 422s a partial/stale list and
   * reassigns `sort_order` = index).
   */
  async reorderItems(
    estimateId: string,
    section: EstimateSection,
    itemIds: string[]
  ): Promise<void> {
    await this.patch(
      `/v1/estimates/${estimateId}/items/reorder`,
      this.formatJsonApiData('estimate_item_orders', {
        section,
        item_ids: itemIds,
      })
    );
  }

  /**
   * Download an estimate as a file (PDF / Excel / Text). The endpoint streams
   * the raw file body (not JSON:API), so we bypass `BaseApiService` — which
   * forces `Content-Type: application/vnd.api+json` and parses the response as
   * JSON — and issue an authenticated raw `fetch`, returning the blob plus the
   * server-supplied filename parsed from `Content-Disposition`. Export is free
   * (no Pass gate, no token cost) and only valid once the estimate is `ready`.
   */
  async export(
    estimateId: string,
    format: EstimateExportFormat
  ): Promise<EstimateExportResult> {
    const baseUrl = resolveApiUrl();
    const token = apiClient.getToken();
    const response = await fetch(
      `${baseUrl}/api/v1/estimates/${estimateId}/export?format=${format}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: '*/*',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Estimate export failed (${response.status})`);
    }
    const blob = await response.blob();
    const filename = parseFilename(
      response.headers.get('Content-Disposition'),
      `estimate-${estimateId}.${format}`
    );
    return { blob, filename };
  }
}

/**
 * Parse the download filename from a `Content-Disposition` header, handling
 * both the plain `filename="…"` and RFC 5987 `filename*=UTF-8''…` forms.
 * Falls back to the provided default when the header is missing or malformed.
 */
function parseFilename(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // Malformed percent-encoding — fall through to the plain form / fallback.
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(disposition);
  return plainMatch?.[1]?.trim() || fallback;
}

export const estimateApi = new EstimateService();

/** API error code returned on a 409 when an edited estimate is regenerated. */
export const COST_ESTIMATE_OVERWRITE_REQUIRED =
  'COST_ESTIMATE_OVERWRITE_REQUIRED';
